export interface ValidationError {
  code: string;
  detail?: string;
  source?: {
    pointer?: string;
  };
  meta?: {
    supplier_id?: string;
    supplier_name?: string;
  };
}

export interface ValidationErrors {
  errors?: ValidationError[];
}

export type GenericAPIError<T = string> = ResponseError<{
  errors: {
    code: T;
  }[];
}>;

export class ResponseError<TData = unknown> extends Error {
  response: Response;
  request: Request;
  data: TData;
  status: number;

  constructor(res: Response, data: TData, request: Request) {
    super(`${request.method} ${request.url} failed with status ${res.status} – ${res.statusText}`);
    this.data = data;
    this.response = res;
    this.status = res.status;
    this.request = request;
    this.name = 'ResponseError';
  }
}

export const isResponseError = <Data = unknown>(error: unknown): error is ResponseError<Data> =>
  error instanceof ResponseError;

export const isValidationError = (
  error: ResponseError
): error is ResponseError<ValidationErrors> => {
  if (error.data === null || typeof error.data !== 'object' || !('errors' in error.data)) {
    return false;
  }
  const data: { errors?: unknown } = error.data;
  return Array.isArray(data.errors);
};

// NOTE: an AbortError so a dismissed SCA prompt reads as a user abort, not a failure:
// `isAbortError` call sites skip the error toast, and Sentry filters drop it. Callers
// stay SCA-agnostic.
// Use `instanceof SensitiveActionCancelledError` if a site ever needs to tell them apart.
export class SensitiveActionCancelledError extends DOMException {
  constructor(message = 'Sensitive action cancelled') {
    super(message, 'AbortError');
  }
}

export const isNotSensitiveActionCancelled = (error: unknown): boolean =>
  !(error instanceof SensitiveActionCancelledError);
